import React, { useContext } from 'react';
import strings from '../../strings/strings.json';
import { MainSectionWithGaps } from 'components/sections/MainSection';
import TabStructureComponent from 'components/tabs/TabStructureComponent';
import { TabDefinition } from 'components/tabs/Tabs';
import ScheduleIcon from 'assets/icons/navigation/ScheduleIcon';
import OrganisationIcon from 'assets/icons/navigation/OrganisationIcon';
import OACoursesTable from './courses/OACoursesTable';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Header from 'components/sections/Header';
import BuildIcon from 'assets/icons/BuildIcon';
import { OADashboardContext, OADashboardProvider } from 'contextProviders/OADashboardContext';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import useWindowWidth from 'hooks/useWindowWidth';
import LiveCoursesTable from './courses/LiveCoursesTable';
import { OAUsersTable } from './trainers/OAUsersTable';
import { Flex } from 'components/Elements';

type State = {
    activeTabId: string;
    activeRowId?: string;
};

export default function OADashboard(): JSX.Element {
    return (
        <OADashboardProvider>
            <Outlet />
        </OADashboardProvider>
    );
}

export function OADashboardContent(): JSX.Element {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { activeTabId, activeRowId } = (state as State) ?? {};

    const liveCourses = useContext(OADashboardContext).liveCourses!;
    const organisation = useContext(OADashboardContext).organisation!;
    const { errors } = useContext(OADashboardContext);
    const { belowTabletWidth } = useWindowWidth();

    const tabDefinitions: TabDefinition[] = [
        {
            id: 'courses',
            name: strings.OADashboard.tabTitles.courses,
            icon: BuildIcon,
            content: <OACoursesTable activeRowCourseId={activeRowId} />,
        },
        {
            id: 'liveCourses',
            name: strings.OADashboard.tabTitles.liveCourses,
            icon: ScheduleIcon,
            content: (
                <LiveCoursesTable
                    liveCourses={liveCourses}
                    activeRowCourseId={activeRowId}
                    hideTrainerColumn={false}
                />
            ),
        },
        {
            id: 'users',
            name: strings.OADashboard.tabTitles.users,
            icon: OrganisationIcon,
            content: <OAUsersTable />,
        },
    ];

    if (errors)
        return (
            <>
                <Header title={strings.OADashboard.getOrgDataError.dashboardHeader} />
                <MainSectionWithGaps>
                    <Notification
                        notificationType={NotificationType.DANGER}
                        title={strings.OADashboard.getOrgDataError.title}
                        description={strings.OADashboard.getOrgDataError.description}
                        big={belowTabletWidth ? false : true}
                        maxWidth="100%"
                    />
                </MainSectionWithGaps>
            </>
        );

    return (
        <>
            <Header title={organisation.name} logoS3Filepath={organisation.logoS3Filepath} roleSwitch />
            <MainSectionWithGaps>
                <Flex>
                    <Notification
                        notificationType={NotificationType.INFO}
                        title={strings.OADashboard.welcome.title}
                        description={strings.OADashboard.welcome.description}
                        buttonToHelp={{
                            text: strings.OAScheduledCoursesTable.viewLiveCoursesButton.title,
                            aria: strings.OAScheduledCoursesTable.viewLiveCoursesButton.aria,
                            action: () => {
                                navigate(`/public/organisation/${organisation.publicName}`);
                            },
                        }}
                    />
                </Flex>
                <TabStructureComponent tabs={tabDefinitions} initialActiveTabId={activeTabId} />
            </MainSectionWithGaps>
        </>
    );
}
